import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  verifyBeforeUpdateEmail,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  getAdditionalUserInfo,
  ActionCodeSettings,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { storeEmailForSignIn, getEmailForSignIn, clearEmailForSignIn } from '../utils/emailStorage';
import { getRedirectUrl, getIsExtension } from '../utils/urlParams';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  generateCustomToken: () => Promise<string>;
  changeEmail: (newEmail: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  sendEmailLink: (email: string) => Promise<void>;
  completeEmailLink: (url: string) => Promise<{ isNewUser: boolean }>;
  completeEmailLinkWithEmail: (url: string, email: string) => Promise<{ isNewUser: boolean }>;
  setPassword: (newPassword: string) => Promise<void>;
  isRecentlyAuthenticated: boolean;
  markRecentlyAuthenticated: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecentlyAuthenticated, setIsRecentlyAuthenticated] = useState(false);
  const reauthTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const RECENT_AUTH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

  const markRecentlyAuthenticated = useCallback(() => {
    setIsRecentlyAuthenticated(true);
    if (reauthTimerRef.current) clearTimeout(reauthTimerRef.current);
    reauthTimerRef.current = setTimeout(() => {
      setIsRecentlyAuthenticated(false);
    }, RECENT_AUTH_THRESHOLD_MS);
  }, [RECENT_AUTH_THRESHOLD_MS]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);

      // Check if login is recent enough
      if (user?.metadata?.lastSignInTime) {
        const lastSignIn = new Date(user.metadata.lastSignInTime).getTime();
        const elapsed = Date.now() - lastSignIn;
        if (elapsed < RECENT_AUTH_THRESHOLD_MS) {
          setIsRecentlyAuthenticated(true);
          if (reauthTimerRef.current) clearTimeout(reauthTimerRef.current);
          reauthTimerRef.current = setTimeout(() => {
            setIsRecentlyAuthenticated(false);
          }, RECENT_AUTH_THRESHOLD_MS - elapsed);
        } else {
          setIsRecentlyAuthenticated(false);
        }
      } else {
        setIsRecentlyAuthenticated(false);
      }
    });

    return () => {
      unsubscribe();
      if (reauthTimerRef.current) clearTimeout(reauthTimerRef.current);
    };
  }, [RECENT_AUTH_THRESHOLD_MS]);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    markRecentlyAuthenticated();
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const sendEmailLink = async (email: string) => {
    // Rebuild the callback URL from captured params (the address bar has
    // already been cleaned by urlParams, so window.location.search is empty).
    const callbackParams = new URLSearchParams();
    const redirectUrl = getRedirectUrl();
    const isExtension = getIsExtension();
    if (redirectUrl) callbackParams.set('redirect_url', redirectUrl);
    if (isExtension) callbackParams.set('extension', 'true');
    const qs = callbackParams.toString();

    const actionCodeSettings: ActionCodeSettings = {
      url: window.location.origin + '/auth/email-callback' + (qs ? `?${qs}` : ''),
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    storeEmailForSignIn(email);
  };

  const completeEmailLink = async (url: string): Promise<{ isNewUser: boolean }> => {
    if (!isSignInWithEmailLink(auth, url)) {
      throw new Error('Invalid email link');
    }

    const email = getEmailForSignIn();
    if (!email) {
      throw new Error('EMAIL_NOT_FOUND');
    }

    const result = await signInWithEmailLink(auth, email, url);
    clearEmailForSignIn();
    markRecentlyAuthenticated();

    const additionalInfo = getAdditionalUserInfo(result);
    return { isNewUser: additionalInfo?.isNewUser ?? true };
  };

  const completeEmailLinkWithEmail = async (url: string, email: string): Promise<{ isNewUser: boolean }> => {
    if (!isSignInWithEmailLink(auth, url)) {
      throw new Error('Invalid email link');
    }

    const result = await signInWithEmailLink(auth, email, url);
    clearEmailForSignIn();
    markRecentlyAuthenticated();

    const additionalInfo = getAdditionalUserInfo(result);
    return { isNewUser: additionalInfo?.isNewUser ?? true };
  };

  const setPasswordFn = async (newPassword: string) => {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to set password');
    }
    await firebaseUpdatePassword(auth.currentUser, newPassword);
  };

  const generateCustomToken = async (): Promise<string> => {
    if (!user) {
      throw new Error('User must be authenticated to generate custom token');
    }

    try {
      // Use Firebase Functions endpoint
      // This will automatically use your deployed Firebase Function
      const functionUrl = 'https://us-central1-betabook-683f0.cloudfunctions.net/generateToken';
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({ uid: user.uid })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to generate custom token`);
      }

      const data = await response.json();
      if (!data.success || !data.customToken) {
        throw new Error(data.error || 'Invalid response from token service');
      }

      return data.customToken;
    } catch (error) {
      console.error('Error generating custom token:', error);
      throw error;
    }
  };

  const changeEmail = async (newEmail: string) => {
    if (!user) {
      throw new Error('User must be authenticated to change email');
    }

    try {
      await verifyBeforeUpdateEmail(user, newEmail);
    } catch (error: any) {
      console.error('Error changing email:', error);
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('Email is already in use');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email address');
      }
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!user || !user.email) {
      throw new Error('User must be authenticated to change password');
    }

    try {
      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await firebaseUpdatePassword(user, newPassword);
    } catch (error: any) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect current password');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('New password is too weak');
      }
      throw error;
    }
  };

  const deleteAccount = async () => {
    if (!user || !user.email) {
      throw new Error('User must be authenticated to delete account');
    }

    try {
      // Delete the user account directly via Firebase Auth client SDK.
      // The Firebase "Delete User Data" extension will automatically
      // clean up all associated Firestore data.
      await deleteUser(user);
    } catch (error: any) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password');
      }
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signOut,
    generateCustomToken,
    changeEmail,
    changePassword,
    deleteAccount,
    sendEmailLink,
    completeEmailLink,
    completeEmailLinkWithEmail,
    setPassword: setPasswordFn,
    isRecentlyAuthenticated,
    markRecentlyAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
